import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Download, FileText, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const noun = data.errors.length ? "with errors" : "clean";
      toast.success(`Imported: ${data.created} new, ${data.updated} updated (${noun})`);
      onImported?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const templateHref = `${BACKEND}/api/admin/products/bulk-template.csv`;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
      <DialogContent className="rounded-2xl max-w-xl bg-bone border-borderSubtle" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Bulk import products</DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex items-center gap-3 text-xs">
          <a
            href={templateHref}
            className="inline-flex items-center gap-1.5 text-oxblood hover:text-charcoal underline underline-offset-2"
            data-testid="bulk-template-link"
          >
            <Download size={12}/> Download CSV template
          </a>
          <span className="text-stone">·</span>
          <span className="text-stone">Existing products (matched by slug) are updated.</span>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onPick}
          data-testid="bulk-import-input"
        />
        <div
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`p-8 rounded-2xl border border-dashed cursor-pointer text-center transition-colors ${
            dragOver ? "border-oxblood bg-white" : "border-borderSubtle bg-white hover:border-charcoal"
          } ${busy ? "opacity-70 cursor-wait" : ""}`}
          data-testid="bulk-import-dropzone"
        >
          <FileText size={22} strokeWidth={1.5} className="mx-auto text-stone mb-3"/>
          <p className="font-medium">
            {busy ? "Importing…" : "Drag & drop a CSV or click to browse"}
          </p>
          <p className="text-[11px] text-stone mt-1">
            Columns: name, slug, sku, category, unit_label, price, dose_mg, description, stock, in_stock, featured, image_url
          </p>
        </div>

        {result && (
          <div className="mt-4 border border-borderSubtle bg-white p-4" data-testid="bulk-import-result">
            <div className="flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-2 text-oxblood">
                <CheckCircle2 size={14} strokeWidth={2}/>
                <span className="font-mono">{result.created}</span> created
              </span>
              <span className="text-stone">·</span>
              <span className="inline-flex items-center gap-2 text-charcoal">
                <Upload size={14} strokeWidth={2}/>
                <span className="font-mono">{result.updated}</span> updated
              </span>
              {result.errors.length > 0 && (
                <>
                  <span className="text-stone">·</span>
                  <span className="inline-flex items-center gap-2 text-oxblood">
                    <XCircle size={14} strokeWidth={2}/>
                    <span className="font-mono">{result.errors.length}</span> errors
                  </span>
                </>
              )}
            </div>
            {result.errors.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto space-y-1" data-testid="bulk-import-errors">
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-stone font-mono">
                    Row {err.row}: {err.message}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
